import React from 'react';
import { useT } from 'client/i18n/hooks/useT';
import { useLocale } from 'client/i18n/hooks/useLocale';
import { useCurrentUserCtx } from 'client/contexts/CurrentUser';
import { useUpdateUserLocale } from 'client/hooks/user/useUpdateLocale';
import { LocalesInfo } from 'client/i18n/types';
import locales from 'client/i18n/locales.json';
import {
  LanguageButton,
  LanguageGrid,
  Modal,
  ModalBody,
  ModalAsideContent,
  MutedText,
} from './styles';

const pseudoInfo = {
  pseudo: {
    smartling: 'pseudo',
    displayName: 'Pseudo',
    displayNameEn: 'Pseudo',
  },
} as LocalesInfo;

type LanguageSelectionModalProps = {
  onClose: () => void;
};

export function LanguageSelectionModal({
  onClose,
}: LanguageSelectionModalProps): React.ReactElement {
  const {
    state: { userId },
  } = useCurrentUserCtx();
  const t = useT();
  const { locale: currentLocale, changeLocale, showPseudoLang } = useLocale();
  const { updateUserLocale } = useUpdateUserLocale();
  const [
    currentLangRef,
    setCurrentLangRef,
  ] = React.useState<HTMLLIElement | null>(null);

  React.useEffect(() => {
    if (currentLangRef) currentLangRef.focus();
  }, [currentLangRef]);

  const handleSelectLanguage = (newLocale: string) => {
    // TODO https://anchorfm.atlassian.net/browse/WHEEL-807: Find Solution Without Utilizing GA Global Object
    if (window.ga !== undefined) {
      const gaEventParams = {
        eventCategory: 'Profile Page Subscribe',
        eventAction: 'Language Selector',
        eventLabel: newLocale,
      };

      // TODO https://anchorfm.atlassian.net/browse/WHEEL-807: Find Solution Without Utilizing GA Global Object
      window.ga('send', 'event', gaEventParams);
    }

    changeLocale(newLocale);
    if (userId) updateUserLocale({ userId, locale: newLocale });
    onClose();
  };

  // Allow users select a language using the keyboard
  // by pressing Enter (13) or Space (32) keys.
  const handleKeyPress = (keyCode: number, newLocale: string) => {
    if (keyCode === 13 || keyCode === 32) {
      handleSelectLanguage(newLocale);
    }
  };

  const localesList = {
    ...locales,
    ...(showPseudoLang ? pseudoInfo : {}),
  };

  return (
    <Modal isShowing isShowingCloseButton onClose={onClose}>
      <ModalAsideContent>
        {t(
          'LanguageSelector.LanguageSelectionModal.Title',
          'Choose a language',
          'Language selection modal title'
        )}
      </ModalAsideContent>
      <ModalBody as="div">
        <LanguageGrid>
          {Object.entries(localesList as LocalesInfo).map(
            ([locale, localeInfo]) => {
              return (
                <li>
                  <LanguageButton
                    key={locale}
                    ref={
                      locale === currentLocale
                        ? (node: HTMLLIElement) => setCurrentLangRef(node)
                        : null
                    }
                    onClick={() => handleSelectLanguage(locale)}
                    onKeyPress={(event: React.KeyboardEvent) =>
                      handleKeyPress(event.charCode, locale)
                    }
                  >
                    <span>{localeInfo.displayName}</span>
                    <MutedText>{localeInfo.displayNameEn}</MutedText>
                  </LanguageButton>
                </li>
              );
            }
          )}
        </LanguageGrid>
      </ModalBody>
    </Modal>
  );
}
